// src/components/sales/pages/sales-templates/SalesTemplatesPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Plus, RefreshCw, Pencil, Trash2, X, LayoutTemplate,Mail } from "lucide-react";
import SalesTemplatesView from "./SalesTemplatesView";
import SalesTemplateModal from "./SalesTemplateModal";
import { salesTemplatesService } from "../../../../services/salesTemplates.service";

const CHANNEL_FILTERS = ["All", "Email", "WhatsApp", "SMS"];

export default function SalesTemplatesPage({ logout }) {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [filterChannel, setFilterChannel] = useState("All");

  // banner message بدل alert
  const [uiMessage, setUiMessage] = useState({ type: "", text: "" });
  const setError = (text) => setUiMessage({ type: "error", text: text || "Request failed" });
  const setSuccess = (text) => setUiMessage({ type: "success", text: text || "Done" });
  const clearMessage = () => setUiMessage({ type: "", text: "" });

  const isForbidden = (e) => String(e?.message || "").toLowerCase().startsWith("http 403");
  const isNotFound = (e) => String(e?.message || "").toLowerCase().startsWith("http 404");

  const safeId = (t) => {
    const v = t?.id ?? t?.templateId ?? t?.salesTemplateId;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const load = async () => {
    setLoading(true);
    clearMessage();
    try {
      const payload =
        filterChannel && filterChannel !== "All"
          ? { channelType: filterChannel }
          : {};

      const res = await salesTemplatesService.list(payload);

      const list = Array.isArray(res) ? res : (res?.data ?? res?.result ?? res?.items ?? []);
      setTemplates(Array.isArray(list) ? list : []);

      // keep selected if exists
      if (selected) {
        const sid = safeId(selected);
        const fresh = (Array.isArray(list) ? list : []).find((x) => safeId(x) === sid);
        setSelected(fresh || null);
      }
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        logout?.();
        return;
      }
      setError(e?.message || "Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterChannel]);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setModalOpen(true);
  };

  const deleteTemplate = async (row) => {
    const id = safeId(row);
    if (id == null) return setError("Invalid template id");

    const ok = window.confirm("Are you sure you want to delete this template?");
    if (!ok) return;

    setLoading(true);
    clearMessage();
    try {
      await salesTemplatesService.remove(id);
      setSuccess("Template deleted successfully.");
      if (safeId(selected) === id) setSelected(null);
      await load();
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        logout?.();
        return;
      }
      if (isForbidden(e)) setError("HTTP 403: You don’t have permission to delete templates.");
      else if (isNotFound(e)) setError("HTTP 404: Template not found (already deleted).");
      else setError(e?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const headers = useMemo(() => ["Template", "Channel", "Active", "Actions"], []);

  return (
    <div className="text-white">
      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-xl bg-white/10 border border-white/20 backdrop-blur-xl">
            <Mail className="w-6 h-6 text-pink-200" />
          </div>
          <div>
            <h2 className="text-xl font-bold">Pre-Sales Templates</h2>
            <p className="text-xs text-white/70">Manage templates by channel</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <select
            value={filterChannel}
            onChange={(e) => setFilterChannel(e.target.value)}
            disabled={loading}
            className="px-3 py-2 rounded-xl bg-white/10 border border-white/20 text-white backdrop-blur-lg outline-none"
          >
            {CHANNEL_FILTERS.map((c) => (
              <option key={c} value={c} className="text-black">
                {c}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={openCreate}
            className="px-4 py-2 rounded-xl bg-purple-600/80 hover:bg-purple-700 text-white flex items-center gap-2 backdrop-blur-lg disabled:opacity-50"
            disabled={loading}
          >
            <Plus className="w-5 h-5" />
            Add Template
          </button>

          <button
            onClick={load}
            className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white flex items-center gap-2 backdrop-blur-lg disabled:opacity-50"
            disabled={loading}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Message Banner */}
      {uiMessage?.text ? (
        <div
          className={[
            "mb-6 rounded-2xl px-4 py-3 text-sm border backdrop-blur-xl flex items-start justify-between gap-3",
            uiMessage.type === "error"
              ? "border-red-500/30 bg-red-500/10 text-red-200"
              : "border-emerald-500/30 bg-emerald-500/10 text-emerald-200",
          ].join(" ")}
        >
          <div className="whitespace-pre-wrap">{uiMessage.text}</div>
          <button
            type="button"
            onClick={clearMessage}
            className="shrink-0 p-2 rounded-lg bg-white/10 hover:bg-white/15 border border-white/10"
            title="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : null}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* List */}
        <div className="backdrop-blur-xl bg-white/10 rounded-2xl shadow-[0_0_40px_rgba(255,0,255,0.20)] border border-white/20 overflow-hidden">
          <div className="p-6 bg-gradient-to-r from-pink-500/20 to-purple-600/30 backdrop-blur-xl flex items-center justify-between">
            <h3 className="text-lg font-bold">Templates</h3>
            <span className="text-sm text-gray-200">Total: {templates?.length || 0}</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-white">
              <thead className="bg-white/10">
                <tr>
                  {headers.map((h) => (
                    <th key={h} className="px-4 py-3 text-right text-sm font-semibold">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-white/10">
                {(templates || []).map((t) => {
                  const id = safeId(t);
                  const isSelected = safeId(selected) === id;
                  const active = typeof t?.isActive === "boolean" ? t.isActive : null;

                  return (
                    <tr
                      key={id ?? `${t.templateName || "row"}-${t.channelType || ""}`}
                      className={[
                        "hover:bg-white/10 transition cursor-pointer",
                        isSelected ? "bg-white/10" : "",
                      ].join(" ")}
                      onClick={() => setSelected(t)}
                    >
                      <td className="px-4 py-3 text-right">
                        <div className="font-semibold">{t.templateName || "-"}</div>
                        <div className="text-xs text-gray-300 truncate">ID: {id ?? "-"}</div>
                      </td>

                      <td className="px-4 py-3 text-right">
                        <span className="px-2 py-1 rounded text-xs bg-white/10 border border-white/20">
                          {t.channelType || "-"}
                        </span>
                      </td>

                      <td className="px-4 py-3 text-right">
                        {active === null ? (
                          <span className="text-xs text-white/50">—</span>
                        ) : (
                          <span
                            className={[
                              "px-2 py-1 rounded text-xs border",
                              active
                                ? "bg-emerald-500/10 border-emerald-400/30 text-emerald-300"
                                : "bg-red-500/10 border-red-400/30 text-red-300",
                            ].join(" ")}
                          >
                            {active ? "Active" : "Inactive"}
                          </span>
                        )}
                      </td>

                      <td className="px-4 py-3 text-right">
                        <div className="flex gap-2 justify-end" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => openEdit(t)}
                            className="px-3 py-1.5 rounded-lg bg-pink-600/80 hover:bg-pink-700 text-white text-xs backdrop-blur-lg flex items-center gap-1 disabled:opacity-50"
                            disabled={loading || id == null}
                          >
                            <Pencil className="w-4 h-4" />
                            Edit
                          </button>

                          <button
                            onClick={() => deleteTemplate(t)}
                            className="px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 border border-red-400/30 text-red-100 text-xs backdrop-blur-lg flex items-center gap-1 disabled:opacity-50"
                            disabled={loading || id == null}
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {!loading && (!templates || templates.length === 0) && (
                  <tr>
                    <td className="px-4 py-10 text-center text-gray-200" colSpan={4}>
                      No templates yet.
                    </td>
                  </tr>
                )}

                {loading && (
                  <tr>
                    <td className="px-4 py-10 text-center text-gray-200" colSpan={4}>
                      Loading...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Details */}
        <SalesTemplatesView template={selected} />
      </div>

      {/* Modal */}
      <SalesTemplateModal
        open={modalOpen}
        editing={editing}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSaved={async () => {
          setModalOpen(false);
          setEditing(null);
          await load();
          setSuccess(editing?.id ? "Template updated successfully." : "Template created successfully.");
        }}
      />
    </div>
  );
}
